import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, Typography, CircularProgress, Box, Grid, Button, Chip, CardActions, Divider,
   Dialog, DialogTitle, DialogContent, IconButton, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Organizationapplied, OrganizationapplyprojectsDetails } from "../../../../../helper/base_url";

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'accepted':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const AppliedJobsList = () => {
  const [appliedProjects, setAppliedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchAppliedJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Organizationapplied}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAppliedProjects(response.data.appliedProjects || []);
    } catch (error) {
      //console.error('Error fetching applied jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppliedJobs();
  }, []);

  const fetchProjectsDetails = async (projectId) => {
    try {
      setDetailsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${OrganizationapplyprojectsDetails}/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedJobDetails(response.data.data);
      setModalOpen(true);
    } catch (error) {
      //console.error('Failed to fetch job details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (appliedProjects.length === 0) {
    return (
      <Typography variant="h6" align="center" mt={6}>
        No applied Projects.
      </Typography>
    );
  }

  return (
    <Box p={3} width="100%">
      <Typography variant="h2" gutterBottom fontSize="1.4rem">
        My Applied Projects
      </Typography>
      <Grid container spacing={3} marginTop="10px">
        {appliedProjects.map((job, index) => (
          <Grid item xs={12} sm={6} md={6} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height : '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="body" component="div" gutterBottom fontSize="1rem" fontWeight={400}>
                  {job.title}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {job.company}
                </Typography>
               <Typography variant="body2" sx={{ mb: 1 }}>
                <a
                    href={job.website?.startsWith('http') ? job.website : `https://${job.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {job.website}
                </a>
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Chip
                  label={job.statusOfApplication}
                  color={getStatusColor(job.statusOfApplication)}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Applied on: {new Date(job.appliedDate).toLocaleDateString()}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Button variant="outlined" size="small" onClick={() => fetchProjectsDetails(job.projectId)}>
                  View Details
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Job Details Dialog */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize:"1rem" }}
        >
          Project Details
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : selectedJobDetails ? (
            <>
            {/* Use correct nested keys based on combinedDetails */}
            <Typography variant="h6" gutterBottom>
           {selectedJobDetails.postproject.projectTitle || 'N/A'}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                {selectedJobDetails.postproject.companyName || 'N/A'}
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" gutterBottom>
                <strong>Company Overview:</strong> {selectedJobDetails.postproject.companyOverview || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Website / LinkedIn:</strong> {selectedJobDetails.postproject.websiteOrLinkedIn || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Domain:</strong> {selectedJobDetails.postproject.domain || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Project Summary:</strong> {selectedJobDetails.postproject.projectSummary || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Expected Deliverables:</strong> {selectedJobDetails.postproject.expectedDeliverables || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Preferred Tech Stack:</strong> {selectedJobDetails.postproject.preferredTechStack || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Preferred Programming Languages:</strong> {selectedJobDetails.postproject.preferredProgrammingLanguages || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Project Duration:</strong> {selectedJobDetails.postproject.projectDuration || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Weekly Time Commitment:</strong> {selectedJobDetails.postproject.weeklyTimeCommitment || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Mode Of Work:</strong> {selectedJobDetails.postproject.modeOfWork || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Desired Start Date:</strong>{' '}
                {selectedJobDetails.postproject.desiredStartDate
                ? new Date(selectedJobDetails.postproject.desiredStartDate).toLocaleDateString()
                : 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Application Deadline:</strong>{' '}
                {selectedJobDetails.postproject.applicationDeadline
                ? new Date(selectedJobDetails.postproject.applicationDeadline).toLocaleDateString()
                : 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Compensation:</strong> {selectedJobDetails.postproject.compensation || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
                <strong>Confidentiality / IP Clauses:</strong> {selectedJobDetails.postproject.confidentialityOrIPClauses || 'N/A'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom><strong>File Information</strong></Typography>
            <Typography variant="body2" gutterBottom>
            <strong>File Path:</strong> {selectedJobDetails.postproject.path || 'N/A'}
            </Typography>
            <Typography variant="body2" gutterBottom>
            {selectedJobDetails.postproject.s3Url ? (
                <a href={selectedJobDetails.postproject.s3Url} target="_blank" rel="noopener noreferrer">
                View File
                </a>
            ) : (
                'N/A'
            )}
            </Typography>
            <Typography variant="body2" gutterBottom>
            <strong>Original File Name:</strong> {selectedJobDetails.postproject.originalname || 'N/A'}
            </Typography>
          </>
        ) : (
        <Typography>No details available.</Typography>
        )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppliedJobsList;
